import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PACKAGE_STATUS_LABELS,
  type Concierge,
  type Page,
  type PackageSummary,
  type Resident,
  type SubscriberDetail,
  type SubscriberEntitlementsView,
  type SystemLogEntry,
} from '@azko/shared';

import { api } from '../../lib/api.js';
import { formatPhone, formatReceived, statusTone } from '../../lib/format.js';
import { useSession } from '../../lib/session.js';
import { LogFeed } from '../../components/LogFeed.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingRows,
  PageHeader,
  StatCard,
  StatusBadge,
  cx,
} from '../../components/ui.js';

/**
 * A conta do assinante por dentro — o console de suporte.
 *
 * O backend disto existia, pronto e testado, e nao havia tela que o chamasse:
 * `AdminRouter` monta os MESMOS routers do sindico sob
 * `/admin/condominios/:id/...`, com o tenant vindo da URL em vez do membership.
 * Sem esta pagina, responder "o aviso do 302 saiu?" exigia abrir o banco.
 *
 * SOMENTE LEITURA, e isso e uma decisao e nao uma etapa que faltou.
 *
 * O backend delega escrita a quem tem `plataforma:condominios:operar`, e um dia
 * esta tela pode oferecer isso. Mas as tres acoes que o suporte mais pediria —
 * registrar encomenda, confirmar retirada, cancelar — sao justamente as que o
 * router NAO delega, porque disparam WhatsApp para um morador de verdade e
 * consomem cota do plano: efeito que sai da conta da Azko e chega em terceiro.
 * Ler resolve o caso real do atendimento. Escrever resolve o caso imaginado.
 *
 * As abas seguem a ordem das perguntas que chegam ao suporte, nao a ordem do
 * menu do cliente: primeiro o retrato da conta, depois as pessoas (morador e
 * porteiro sao 90% das ligacoes), depois as encomendas, e o log por ultimo —
 * ele e para quando as quatro primeiras nao explicaram.
 */

const TABS = ['geral', 'moradores', 'porteiros', 'encomendas', 'log'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  geral: 'Visao geral',
  moradores: 'Moradores',
  porteiros: 'Porteiros',
  encomendas: 'Encomendas',
  log: 'Log do predio',
};

export function CondominiumPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { staff } = useSession();
  const [tab, setTab] = useState<Tab>('geral');

  const podeAbrir = staff?.permissions.includes('plataforma:condominios:visualizar') ?? false;

  const { data: conta, isLoading } = useQuery({
    queryKey: ['admin', 'assinante', id],
    queryFn: () => api.get<SubscriberDetail>(`/admin/assinantes/${id}`),
  });

  if (!podeAbrir) {
    return (
      <>
        <PageHeader title="Conta do assinante" />
        <Alert>
          Seu perfil nao alcanca a conta dos assinantes por dentro. Peca
          `plataforma:condominios:visualizar` a quem administra a plataforma.
        </Alert>
      </>
    );
  }

  if (isLoading || !conta) {
    return (
      <>
        <PageHeader title="Conta do assinante" />
        <LoadingRows rows={4} />
      </>
    );
  }

  return (
    <>
      <PageHeader title={conta.name} />

      <Card className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="eyebrow">Conta do assinante</p>
            <h1 className="mt-1 truncate text-2xl font-extrabold tracking-tight">
              {conta.name}
            </h1>
            <p className="mt-1 text-sm text-muted">
              {[conta.city, conta.state].filter(Boolean).join('/') || 'sem endereco'}
              {conta.planName && ` • ${conta.planName}`}
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <StatusBadge tone={conta.status === 'active' ? 'accent' : 'warn'}>
              {conta.status === 'active' ? 'no ar' : conta.status}
            </StatusBadge>
            <Link to="/assinantes">
              <Button variant="ghost" className="py-1.5">
                Voltar aos assinantes
              </Button>
            </Link>
          </div>
        </div>

        {/* Um aviso, e nao um rodape discreto: quem abre esta tela esta com o
            cliente no telefone e vai procurar o botao de resolver. Dizer de
            cara que ele nao existe aqui economiza a procura. */}
        <p className="mt-4 rounded-2xl border border-line bg-surface-2 p-3 text-sm text-muted">
          Esta tela e somente leitura. Registrar, entregar ou cancelar encomenda dispara
          WhatsApp para um morador e consome cota do plano — sao acoes do porteiro, nao do
          suporte.
        </p>
      </Card>

      <div className="flex flex-wrap gap-2">
        {TABS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setTab(option)}
            className={
              tab === option
                ? 'rounded-pill border border-accent bg-accent/10 px-3 py-1.5 text-sm font-semibold text-accent'
                : 'rounded-pill border border-line bg-surface-2 px-3 py-1.5 text-sm font-semibold text-muted transition hover:text-fg'
            }
          >
            {TAB_LABELS[option]}
          </button>
        ))}
      </div>

      {tab === 'geral' && <AbaGeral conta={conta} subscriberId={id} />}
      {tab === 'moradores' && <AbaMoradores condominiumId={id} />}
      {tab === 'porteiros' && <AbaPorteiros condominiumId={id} />}
      {tab === 'encomendas' && <AbaEncomendas condominiumId={id} />}
      {tab === 'log' && <AbaLog condominiumId={id} />}
    </>
  );
}

// ---------------------------------------------------------------------------

function AbaGeral({ conta, subscriberId }: { conta: SubscriberDetail; subscriberId: string }) {
  // Reusa a rota de excecoes: ela ja devolve plano, excecao, efetivo e consumo
  // por chave. Uma segunda rota "resumo do plano" seria uma segunda verdade
  // sobre a mesma coisa.
  const { data: ents } = useQuery({
    queryKey: ['admin', 'excecoes', subscriberId],
    queryFn: () =>
      api.get<SubscriberEntitlementsView>(`/admin/assinantes/${subscriberId}/excecoes`),
  });

  const comTeto = (ents?.items ?? []).filter(
    (item) => item.kind === 'limit' && typeof item.effectiveValue === 'number',
  );

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Unidades" value={conta.units} hint="ativas" />
        <StatCard label="Moradores" value={conta.residents} hint="ativos" />
        <StatCard label="Encomendas no mes" value={conta.packagesThisMonth} hint="caixas" />
        <StatCard
          label="Plano"
          value={conta.planName ?? '—'}
          hint={conta.subscriptionStatus ?? 'sem assinatura'}
        />
      </div>

      <Card className="p-5 sm:p-6">
        <p className="eyebrow">Limites em uso</p>
        <h2 className="mt-2 text-xl font-bold tracking-tight">O que esta valendo nesta conta</h2>

        {comTeto.length === 0 ? (
          <p className="mt-3 text-sm text-faint">
            O plano desta conta nao tem limite numerico em nada.
          </p>
        ) : (
          <ul className="mt-4 space-y-2">
            {comTeto.map((item) => {
              const teto = item.effectiveValue as number;
              const pct = teto > 0 ? Math.round(((item.usage ?? 0) / teto) * 100) : 0;

              return (
                <li key={item.key} className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm">
                    {item.label}
                    {item.override && !item.override.expired && (
                      <span className="ml-2 rounded-pill border border-accent/40 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-accent">
                        excecao
                      </span>
                    )}
                  </span>
                  <span
                    className={cx('text-sm tabular-nums', pct >= 100 ? 'font-bold text-warn' : 'text-muted')}
                  >
                    {item.usage ?? 0} de {teto}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </>
  );
}

function AbaMoradores({ condominiumId }: { condominiumId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'condominio', condominiumId, 'moradores'],
    queryFn: () =>
      api.get<Page<Resident>>(`/admin/condominios/${condominiumId}/residents?limit=50`),
  });

  const items = data?.items ?? [];

  return (
    <Card>
      {isLoading && <LoadingRows rows={4} />}
      {!isLoading && items.length === 0 && (
        <EmptyState title="Nenhum morador" description="Esta conta ainda nao cadastrou ninguem." />
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-line-soft">
          {items.map((morador) => (
            <li key={morador.id} className="flex flex-wrap items-center justify-between gap-3 p-4 sm:px-6">
              <span className="min-w-0">
                <span className="block truncate font-semibold">{morador.name}</span>
                <span className="block truncate text-sm text-muted">
                  {morador.units.map((link) => link.unitLabel).join(', ') || 'sem unidade'}
                  {' • '}
                  {/* O telefone e o campo que o suporte mais precisa ler: e por
                      ele que o aviso de encomenda sai, e um digito errado
                      significa um morador que nunca e avisado sem nenhum sinal
                      de que algo esta errado. */}
                  {morador.phoneE164 ? formatPhone(morador.phoneE164) : 'sem telefone'}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-2">
                {morador.pendingPackages > 0 && (
                  <StatusBadge tone="accent">
                    {morador.pendingPackages} aguardando
                  </StatusBadge>
                )}
                {morador.status !== 'active' && (
                  <StatusBadge tone="neutral">{morador.status}</StatusBadge>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {data?.nextCursor && (
        <p className="border-t border-line-soft px-4 py-3 text-sm text-faint sm:px-6">
          Mostrando os 50 primeiros. Use a busca da conta do cliente para achar alguem
          especifico.
        </p>
      )}
    </Card>
  );
}

function AbaPorteiros({ condominiumId }: { condominiumId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'condominio', condominiumId, 'porteiros'],
    queryFn: () =>
      api.get<{ items: Concierge[] }>(`/admin/condominios/${condominiumId}/concierges`),
  });

  const items = data?.items ?? [];

  return (
    <Card>
      {isLoading && <LoadingRows rows={3} />}
      {!isLoading && items.length === 0 && (
        <EmptyState
          title="Nenhum porteiro"
          description="Sem conta de porteiro, ninguem registra encomenda nesta portaria."
        />
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-line-soft">
          {items.map((porteiro) => (
            <li key={porteiro.id} className="flex flex-wrap items-center justify-between gap-3 p-4 sm:px-6">
              <span className="min-w-0">
                <span className="block truncate font-semibold">{porteiro.name}</span>
                <span className="block truncate text-sm text-muted">
                  {porteiro.email}
                  {porteiro.shift && ` • ${porteiro.shift}`}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-2">
                {/* "Nunca entrou" e a diferenca entre conta criada e porteiro
                    trabalhando — e a causa mais comum de "o sistema nao
                    funciona" no primeiro mes. */}
                {porteiro.lastLoginAt === null ? (
                  <StatusBadge tone="warn">nunca entrou</StatusBadge>
                ) : (
                  <span className="text-sm text-faint">
                    ultimo acesso {formatReceived(porteiro.lastLoginAt)}
                  </span>
                )}
                {porteiro.status !== 'active' && (
                  <StatusBadge tone="neutral">{porteiro.status}</StatusBadge>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AbaEncomendas({ condominiumId }: { condominiumId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'condominio', condominiumId, 'encomendas'],
    queryFn: () =>
      api.get<Page<PackageSummary>>(`/admin/condominios/${condominiumId}/packages?limit=25`),
  });

  const items = data?.items ?? [];

  return (
    <Card>
      {isLoading && <LoadingRows rows={4} />}
      {!isLoading && items.length === 0 && (
        <EmptyState title="Nenhuma encomenda" description="Nada registrado nesta portaria." />
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-line-soft">
          {items.map((pacote) => (
            <li key={pacote.id} className="flex flex-wrap items-center justify-between gap-3 p-4 sm:px-6">
              <span className="min-w-0">
                <span className="block truncate font-semibold">
                  #{pacote.code}
                  {pacote.boxCount > 1 && (
                    <span className="ml-2 text-sm font-normal text-muted">
                      {pacote.boxesAwaiting} de {pacote.boxCount} caixas
                    </span>
                  )}
                </span>
                <span className="block truncate text-sm text-muted">
                  {pacote.residentName ?? 'sem destinatario'} • {pacote.unitLabel} •{' '}
                  {formatReceived(pacote.receivedAt)}
                </span>
              </span>

              <StatusBadge tone={statusTone(pacote.status)}>
                {PACKAGE_STATUS_LABELS[pacote.status]}
              </StatusBadge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AbaLog({ condominiumId }: { condominiumId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'condominio', condominiumId, 'log'],
    queryFn: () =>
      api.get<{ items: SystemLogEntry[] }>(
        `/admin/condominios/${condominiumId}/logs?limit=40`,
      ),
  });

  const items = data?.items ?? [];

  return (
    <Card className="overflow-hidden">
      {isLoading && <LoadingRows rows={5} />}
      {!isLoading && items.length === 0 && (
        <EmptyState title="Log vazio" description="Nada registrado neste predio ainda." />
      )}
      {/* O MESMO componente do log do sindico e do log da plataforma. Tres
          telas mostrando a mesma coisa de tres jeitos e como o formato de data
          de uma delas envelhece sem ninguem notar. */}
      {items.length > 0 && <LogFeed entries={items} showSource />}
    </Card>
  );
}
